import {
  CONFIRMATION_CHECKBOX_IDS,
  useOrderFormContext,
} from '../OrderFormContext';
import { useContext } from 'react';
import { LanguageContext } from '@/providers/LanguageContextProvider';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { SafeRichText } from '@/components/SafeRichText/SafeRichText';
import i18n from '@/utils/i18n';

export function AdditionalNote({
  enableLineBreaks = true,
}: {
  enableLineBreaks?: boolean;
}) {
  const {
    fetchedFormInfo,
    termsAndConditionsCheckbox,
    confirmationErrors,
    setConfirmation,
  } = useOrderFormContext();

  // i18n.t() subscribes to nothing, so the React Compiler may freeze these
  // strings at first mount - and the first mount runs before
  // LanguageContextProvider has called changeLanguage. The context locale is
  // already correct in that first render, so resolve against it explicitly.
  const { locale } = useContext(LanguageContext);
  const t = (key: string) => i18n.t(key, { lng: locale });

  const note = fetchedFormInfo?.event?.merchant_order_form_info_text;

  if (!note) {
    return null;
  }

  const id = CONFIRMATION_CHECKBOX_IDS.additionalInfo;
  const hasError = confirmationErrors.additionalInfo;

  return (
    <Card className="tw:p-4 tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:flex-col tw:gap-2">
        <h4 className="tw:text-xl tw:font-semibold">
          {t('EventOrder:eventSpecificTermsTitle')}
        </h4>
        <SafeRichText
          html={note}
          className={cn(
            'tw:text-sm tw:font-medium tw:text-primary tw:dark:text-primary',
            enableLineBreaks && 'tw:whitespace-pre-line',
          )}
        />
      </div>
      <Field orientation="horizontal" data-invalid={hasError || undefined}>
        <Checkbox
          id={id}
          checked={termsAndConditionsCheckbox.additionalInfo}
          onCheckedChange={(checked) =>
            setConfirmation('additionalInfo', !!checked)
          }
          aria-required
          aria-invalid={hasError || undefined}
          className="tw:size-6 tw:border-muted-foreground tw:dark:aria-invalid:border-destructive tw:data-[state=checked]:bg-success-main tw:data-[state=checked]:border-success-main tw:data-[state=checked]:text-white"
        />
        <FieldContent>
          <FieldLabel htmlFor={id}>
            {t('EventOrder:acceptEventSpecificTerms')}
          </FieldLabel>
          {hasError && (
            <FieldError>{t('EventOrder:confirmationRequired')}</FieldError>
          )}
        </FieldContent>
      </Field>
    </Card>
  );
}
